import React, { useState } from "react";
import { CircleHelp } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import HelpMarkdown from "./HelpMarkdown";
import { resolveTopic, splitSections } from "../../help/helpIndex";
import { openHelp } from "../../utils/helpBus";
import { cn } from "../../lib/utils";

/**
 * A small "?" beside an idea that needs explaining. Opens just that part of
 * the help article in a bottom sheet, without leaving the screen, with a way
 * into the full article.
 *
 * topic: "article-id" or "article-id#section-id" (see help/articles.js).
 */
const HelpLink = ({ topic, label, className }) => {
  const [open, setOpen] = useState(false);
  const { article, section } = resolveTopic(topic);
  if (!article) return null;

  const title = section?.title || article.title;
  const content = section
    ? section.body
    : splitSections(article).intro || article.summary;

  return (
    <>
      <button
        type="button"
        aria-label={label || `Help: ${title}`}
        title={label || title}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        className={cn(
          "inline-grid h-5 w-5 shrink-0 place-items-center rounded-full text-muted-foreground/70 align-middle transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        <CircleHelp className="h-4 w-4" />
      </button>
      <DetailSheet
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={section ? article.title : article.summary}
        showDescription={Boolean(section)}
        footer={
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
              onClick={() => {
                setOpen(false);
                requestAnimationFrame(() => openHelp(topic));
              }}
            >
              Full article
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 border-border bg-muted/40 text-base font-semibold hover:bg-muted/60"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        }
      >
        <HelpMarkdown content={content} />
      </DetailSheet>
    </>
  );
};

export default HelpLink;
